import { useRef, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { Button, BUTTON_STYLES, Pagination } from '~/ui';
import PhotoPicker from '~/components/AnnouncementForm/PhotoPickerContainer/PhotoPicker/PhotoPicker';
import { IPhoto as IPhotoPicker } from '~/components/AnnouncementForm/PhotoPickerContainer/PhotoPickerContainer';
import styles from './PhotoPickerWithUpload.module.scss';

export interface IPhoto {
  id: number;
  full_photo_url: string;
  path?: string;
}

interface SelectedPage {
  selected: number;
}

interface PhotoPickerWithUploadProps {
  module: string;
  assetId: number | null;
  selectedImages: IPhoto[];
  onImageChange: (image: IPhoto) => void;
  onCloseClick: () => void;
  onResetClick: () => void;
  initialPhotos?: IPhoto[];
}

const PhotoPickerWithUpload = ({
  module,
  assetId,
  selectedImages,
  onImageChange,
  onCloseClick,
  onResetClick,
}: PhotoPickerWithUploadProps) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (isUploading) {
      setUploadProgress(0);
      progressTimerRef.current = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 85) {
            if (progressTimerRef.current) clearInterval(progressTimerRef.current);
            return 85;
          }
          return prev + Math.random() * 8 + 3;
        });
      }, 300);
    } else {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (uploadProgress > 0) {
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 600);
      }
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isUploading]);
  // Локально загруженные фото (ещё не в photo_storage)
  const [uploadedPhotos, setUploadedPhotos] = useState<IPhoto[]>([]);

  const params = { perPage: 9, page };

  // Загружаем имеющиеся фото из фотохранилища для этого модуля и актива
  const {
    data: storagePhotos,
    isFetching,
    isLoading,
  } = useQuery({
    queryFn: async () => {
      if (!assetId) return { data: [], last_page: 1 };
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}`,
        { params }
      );
      return data;
    },
    queryKey: ['photo-storage', module, assetId, params],
    enabled: !!assetId,
  });

  // Загрузка новых фото
  const { mutateAsync: uploadPhotos } = useMutation(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('photos[]', file);
      });
      const { data } = await instance.post('admin/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data as IPhoto[];
    },
    {
      onError: error => {
        toastError(error);
      },
    }
  );

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files?.length) return;

    setIsUploading(true);
    try {
      const uploaded = await uploadPhotos(Array.from(files));
      console.log('uploaded response:', uploaded);
      if (uploaded?.length) {
        // Добавляем в локальный список и автоматически выбираем
        setUploadedPhotos(prev => [...uploaded, ...prev]);
        uploaded.forEach(photo => {
          onImageChange(photo);
        });
        toastSuccess(t('photo_uploaded'));
      }
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  // Фото из хранилища
  const storagePhotosMapped: IPhotoPicker[] = (storagePhotos?.data || []).map(
    (photo: { id: number; photo_id: number; url: string }) => ({
      id: photo.photo_id,
      full_photo_url: photo.url,
      path: '',
    })
  );

  // Объединяем: локально загруженные (сверху) + из хранилища (без дублей)
  const uploadedIds = new Set(uploadedPhotos.map(p => p.id));
  const allPhotos: IPhotoPicker[] = [
    ...uploadedPhotos.map(p => ({ ...p, path: p.path || '' })),
    ...storagePhotosMapped.filter(p => !uploadedIds.has(p.id)),
  ];

  // Приводим selectedImages к типу PhotoPicker
  const selectedImagesWithPath: IPhotoPicker[] = selectedImages.map(img => ({
    ...img,
    path: img.path || '',
  }));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>{t('choose_photo')}</span>
        <div className={styles.buttons}>
          {selectedImages.length > 0 && (
            <Button
              className={styles.headerBtn}
              buttonStyle={BUTTON_STYLES.EMPTY}
              text={t('discard')}
              onClick={onResetClick}
              color="red"
            />
          )}
          <Button
            className={styles.headerBtn}
            buttonStyle={BUTTON_STYLES.EMPTY}
            type="button"
            text={t('close')}
            onClick={onCloseClick}
          />
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className={styles.hiddenInput}
      />

      {/* Галерея с кнопкой загрузки первой карточкой */}
      <div className={styles.photoPickerWrapper}>
        <PhotoPicker
          photos={allPhotos}
          selectedImages={selectedImagesWithPath}
          onImageClick={onImageChange}
          isFetching={isFetching || isLoading}
          onUploadClick={() => inputRef.current?.click()}
          isUploading={isUploading}
          uploadProgress={Math.min(Math.round(uploadProgress), 100)}
        />
      </div>

      <div className={styles.paginationContainer}>
        <Pagination
          pageCount={storagePhotos?.last_page || 1}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>

      <div className={styles.controls}>
        <Button
          className={styles.confirmButton}
          text={t('confirm')}
          disabled={selectedImages.length === 0}
          onClick={onCloseClick}
        />
      </div>
    </div>
  );
};

export default PhotoPickerWithUpload;
